<template>
	<v-app
		v-resize="onResize"
		id="app-curriculum"
	>
		<!-- Toolbar -->
		<v-card
			:color="inlineStyles.colors.curriculum"
			elevation="4"
			tile
		>
			<!-- Go Home btn (Mobile) -->
			<v-tooltip
				v-if="isXsBreakpoint"
				data-html2canvas-ignore
				right
			>
				<template v-slot:activator="{ on, attrs }">
					<v-btn
						@click="goToHomeRoute()"
						v-bind="attrs"
						v-on="on"
						absolute
						text
						dark
						fab
					>
						<v-icon
							class="icon-chevron"
							large
						>
							mdi-chevron-left
						</v-icon>
						<v-icon
							class="icon-home"
							large
						>
							mdi-home
						</v-icon>
					</v-btn>
				</template>
				<span>Go Home</span>
			</v-tooltip>

			<v-row class="ma-0" align="center" justify="center">
				<v-col
					:align="xsAlignBreakPt"
					cols="12"
					lg="auto"
					md="auto"
					sm="auto"
				>
					<!-- Go Home Button (Desktop) -->
					<span
						v-if="!isXsBreakpoint"
						data-html2canvas-ignore
					>
						<v-tooltip bottom>
							<template v-slot:activator="{ on, attrs }">
								<v-btn
									class="pa-0"
									@click="goToHomeRoute()"
									v-bind="attrs"
									v-on="on"
									rounded
									text
									dark
								>
									<v-icon
										class="icon-chevron"
										large
									>
										mdi-chevron-left
									</v-icon>
									<v-icon
										class="icon-home"
										large
									>
										mdi-home
									</v-icon>
								</v-btn>
							</template>
							<span>Go Home</span>
						</v-tooltip>
					</span>

					<!-- Profile Photo -->
					<v-avatar :size="isXsBreakpoint ? '150' : '115'">
						<img src="@/assets/images/cvProfile.png" alt="Profile"/>
					</v-avatar>
				</v-col>

				<!-- Fullname -->
				<v-col
					class="pa-0"
					:align="xsAlignBreakPt"
					cols="12"
					lg="auto"
					md="auto"
					:sm="smNameBreakPt"
				>
					<h1 class="white--text">Javier Correa Cepeda</h1>
					
					<!-- Options (xs & lg Breakpoint) -->
					<v-container
						v-if="windowWidth<600 || windowWidth>=785"
						:class="isXsBreakpoint ? '' : 'pa-0'"
						data-html2canvas-ignore
					>
						<v-row
							align="center"
							justify="center"
						>
							<!-- Download CV -->
							<v-col cols="auto">
								<v-btn
									@click="generatePDF()"
									:loading="isButtonLoading"
									outlined
									rounded
									dark
								>
									Download CV
								</v-btn>
							</v-col>
							<!-- Change Language -->
							<v-col cols="auto">
								<v-select
									class="select-width"
									:menu-props="{ dark: true }"
									v-model="language"
									:items="languages"
									item-color="white"
									label="Language"
									hide-details
									outlined
									rounded
									dense
									dark
								/>
							</v-col>
						</v-row>
					</v-container>
				</v-col>

				<v-spacer />

				<!-- Contact Info -->
				<v-col
					:align="isXsBreakpoint ? 'space-betweent' : 'end'"
					cols="12"
					lg="auto"
					md="auto"
					sm="auto"
				>
					<v-layout
						v-for="(contact, index) in contacts"
						:key="index"
					>
						<v-flex xl12 xm12 xs12>
							<p class="my-2 white--text">{{ contact.text }}</p>
						</v-flex>
						<v-flex>
							<v-icon
								class="my-2"
								color="white"
							>
								mdi-{{ contact.icon }}
							</v-icon>
						</v-flex>
					</v-layout>
				</v-col>
			</v-row>

			<!-- Options (md Breakpoint) -->
			<v-container class="mt-n4">
				<v-row
					v-if="windowWidth>=600 && windowWidth<785"
					data-html2canvas-ignore
					align="center"
					no-gutters
				>
					<!-- Download CV -->
					<v-btn
						@click="generatePDF()"
						:loading="isButtonLoading"
						outlined
						rounded
						dark
					>
						Download CV
					</v-btn>

					<!-- Horizontal Space Between Button and Select -->
					<v-spacer />
					<v-col></v-col>

					<!-- Change Language -->
					<v-select
						class="no-width"
						:menu-props="{ dark: true }"
						v-model="language"
						:items="languages"
						item-color="white"
						label="Language"
						hide-details
						outlined
						rounded
						dense
						dark
					/>
				</v-row>
			</v-container>
		</v-card>

		<!-- Information -->
		<v-col
			class="information-background"
			cols="12"
		>
			<!-- Description -->
			<h3
				class="title"
				@click="showOrHide(0)"
			>
				{{ information.descriptionLabel[selectedLanguage] }}
				<v-icon
					data-html2canvas-ignore
					:class="rotate(0)"
					class="mt-n1 ml-n4"
					x-large
				>
					mdi-menu-down
				</v-icon>
			</h3>
			<v-divider color="grey" />
			<!-- Description Item -->
			<transition name="slice" mode="in-out" type="transition">
				<div v-if="isInfoVisible[0]" >
					<v-row class="mt-4 mx-6 mb-n1" no-gutters>
						<p class="text-justify">
							{{ information.descriptionItems[selectedLanguage] }}
						</p>
					</v-row>
				</div>
			</transition>

			<!-- Knowledge -->
			<h3 class="title" @click="showOrHide(1)">
				{{ information.knowledgeLabel[selectedLanguage] }}
				<v-icon
					data-html2canvas-ignore
					:class="rotate(1)"
					class="mt-n1 ml-n4"
					x-large
				>
					mdi-menu-down
				</v-icon>
			</h3>
			<v-divider color="grey" />
			<!-- Knowledge List -->
			<transition
				type="transition"
				name="slice"
				mode="in-out"
			>
				<v-row
					v-if="isInfoVisible[1]" 
					class="mb-9"
				>
					<v-col
						v-for="(knowledge, index) in information.knowledgeItems"
						:key="index"
						cols="12"
						lg="3"
						md="3"
						sm="4"
					>
						<v-list
							color="transparent"
							height="10"
						>
							<v-list-item>
								<v-icon>mdi-circle-medium</v-icon>
								<img
									:src="require('@/assets/images/logos/'+knowledge.logo+'.svg')"
									width="20"
								/>
								<v-list-item-title>{{ knowledge.name }}</v-list-item-title>
							</v-list-item>
						</v-list>
					</v-col>
				</v-row>
			</transition>

			<!-- Aptitude -->
			<h3
				class="title"
				@click="showOrHide(2)"
			>
				{{ information.aptitudesLabel }}
				<v-icon
					data-html2canvas-ignore
					:class="rotate(2)"
					class="mt-n1 ml-n4"
					x-large
				>
					mdi-menu-down
				</v-icon>
			</h3>
			<v-divider color="grey" />
			<!-- Aptitude List -->
			<transition
				type="transition"
				name="slice"
				mode="in-out"
			>
				<v-row
					v-if="isInfoVisible[2]"
					class="mb-9"
				>
					<v-col
						v-for="(aptitue, index) in information.aptitudesItems"
						:key="index"
						cols="12"
						lg="3"
						md="3"
						sm="4"
					>
						<v-list
							color="transparent"
							height="10"
						>
							<v-list-item>
								<v-icon>mdi-circle-medium</v-icon>
								<v-list-item-title>
									{{ aptitue[selectedLanguage] }}
								</v-list-item-title>
							</v-list-item>
						</v-list>
					</v-col>
				</v-row>
			</transition>

			<!-- Education -->
			<h3
				class="title"
				@click="showOrHide(3)"
			>
				{{ information.educationLabel[selectedLanguage] }}
				<v-icon
					data-html2canvas-ignore
					:class="rotate(3)"
					class="mt-n1 ml-n4"
					x-large
				>
					mdi-menu-down
				</v-icon>
			</h3>
			<v-divider color="grey" />
			<!-- Education List -->
			<transition
				type="transition"
				name="slice"
				mode="in-out"
			>
				<div v-if="isInfoVisible[3]">
					<v-row
						class="mt-4 mx-6 mb-n1"
						v-for="(education, index) in information.educationItems"
						:key="index"
						no-gutters
					>
						<v-col
							:align="xsAlignBreakPt"
							cols="12"
							lg="3"
							md="4"
							sm="4"
						>
							<!-- Date & Place -->
							<v-list color="transparent">
								<h3>{{ education.date }}</h3>
								<p>{{ education.place }}</p>
							</v-list>
						</v-col>
						<v-col
							:align="xsAlignBreakPt"
							cols="12"
							lg="9"
							md="8"
							sm="8"
						>
							<!-- School & Carreer -->
							<v-list color="transparent">
								<h3>{{ education.carreer[selectedLanguage] }}</h3>
								<p>{{ education.school }}</p>
							</v-list>
						</v-col>
						<v-col
							v-if="index < information.educationItems.length - 1"
							cols="12"
						>
							<v-divider color="grey" />
						</v-col>
					</v-row>
				</div>
			</transition>

			<!-- Professional Experience -->
			<h3
				class="title"
				@click="showOrHide(4)"
			>
				{{ information.experienceLabel[selectedLanguage] }}
				<v-icon
					data-html2canvas-ignore
					:class="rotate(4)"
					class="mt-n1 ml-n4"
					x-large
				>
					mdi-menu-down
				</v-icon>
			</h3>
			<v-divider color="grey" />
			<!-- Professional Experience List -->
			<transition
				type="transition"
				name="slice"
				mode="in-out"
			>
				<div v-if="isInfoVisible[4]">
					<v-row
						class="mt-4 mx-6 mb-n1"
						v-for="(experience, index) in information.experienceItems"
						:key="index"
						no-gutters
					>
						<v-col
							:align="xsAlignBreakPt"
							cols="12"
							lg="3"
							md="4"
							sm="4"
						>
							<v-list color="transparent">
								<h3>{{ experience.date[selectedLanguage] }}</h3>
								<p>{{ experience.place }}</p>
							</v-list>
						</v-col>
						<v-col
							:align="xsAlignBreakPt"
							cols="12"
							lg="9"
							md="8"
							sm="8"
						>
							<v-list
								color="transparent"
								dense
							>
								<h3>{{ experience.company }}</h3>
								<h4>{{ experience.charge[selectedLanguage] }}</h4>
								<v-list color="transparent">
									<v-list-item
										v-for="(task, taskIndex) in experience.tasks"
										:key="taskIndex"
										class="mb-n4"
									>
										<v-layout>
											<v-flex>
												<v-icon>mdi-circle-medium</v-icon>
											</v-flex>
											<v-flex
												class="text-start"
												xl12
												xm12
												xs12
											>
												<p>{{ task[selectedLanguage] }}</p>
											</v-flex>
										</v-layout>
									</v-list-item>
								</v-list>
							</v-list>
						</v-col>
						<v-col
							v-if="index < information.experienceItems.length - 1"
							cols="12"
						>
							<v-divider color="grey" />
						</v-col>
					</v-row>
				</div>
			</transition>
		</v-col>
	</v-app>
</template>

<script>
import html2pdf from "html2pdf.js"

export default {
	name: "CurriculumView",
	props:{
		inlineStyles: {
			type: Object,
			require: true
		}
	},
	// Data Ordered Alphabetically
	data: () => ({
		isButtonLoading: false,
		isInfoVisible: [true, true, true, true, true],
		contacts: [
			{text: "Mexico", icon: "map-marker"},
			{text: "javier_cc96@hotmail.com", icon: "email"},
			{text: "Javier Correa Cepeda", icon: "linkedin"},
			{text: "www.javi-co.web.app", icon: "web"},
		],
		languages: ["English", "Español"],
		language: "English",
		information: {
			descriptionLabel: {en: "description", es: "descripción"},
			descriptionItems: {
				en: "My aptitudes and interests are focused in Web Development, essentially in FrontEnd, UI and UX. However, my goal is improve my BackEnd knowledge to become a Full Stack Developer, in addition to learning to develop Mobile and Desktop applications. The same way, I like drawing, illustration, image editing and 3D design and modeling; but I would like learn about making animations and motion graphics.",
				es: "Mis aptitudes e intereses están enfocados en el Desarrollo Web, esencialmente en FrontEnd, UI y UX. Sin embargo, mi objetivo es mejorar mis conocimientos en Backend para convertirme en un Desarrollador Full Stack, además de aprender a desarrollar aplicaciones móviles y de escritorio. De la misma manera, me gusta el dibujo, la ilustración, la edición de imágenes y el diseño y modelado 3D; pero me gustaría aprender a realizar animaciones y motion graphics."
			},
			knowledgeLabel: {en: "knowledge", es: "conocimientos"},
			knowledgeItems: [
				{name:"HTML",  logo:"html"},
				{name:"CSS",  logo:"css"},
				{name:"JS",  logo:"js"},
				{name:"SASS",  logo:"sass"},
				{name:"TS",  logo:"ts"},
				{name:"VueJS",  logo:"vue"},
				{name:"Vuetify",  logo:"vuetify"},
				{name:"Git",  logo:"git"},
				{name:"GitHub",  logo:"github"},
				{name:"VS Code",  logo:"VScode"},
				{name:"Scrum",  logo:"scrum"},
				{name:"Blender 3D",  logo:"blender"},
				{name:"Photoshop",  logo:"photoshop"}
			],
			aptitudesLabel: "aptitudes",
			aptitudesItems: [
				{en: "Creativity", es: "Creatividad"},
				{en: "Responsibility", es: "Responsabilidad"},
				{en: "Honesty", es: "Honestidad"},
				{en: "Discipline", es: "Disciplina"},
				{en: "Seriousness", es: "Seriedad"},
				{en: "Perseverance", es: "Perseverancia"},
				{en: "Respect", es: "Respeto"},
				{en: "Puntuality", es: "Puntualidad"},
				{en: "Organization", es: "Organización"}
			],
			educationLabel: {en: "education", es: "educación"},
			educationItems: [
				{
					date: "2017 - 2021",
					place: "Durango, DGO",
					carreer: {
						en: "Software Engineering",
						es: "Ingeniería de Software"
					},
					school: "Universidad Politécnica de Durango"
				},
				{
					date: "2011 - 2014",
					place: "El Salto, DGO",
					carreer: {
						en: "Computer Equipment Maintenance and Support Technician",
						es: "Técnico en Soporte y Mantenimiento de Equipo de Cómputo"
					},
					school: "Centro de Bachillerato Tecnológico Forestal No. 1"
				}
			],
			experienceLabel: {
				en: "professional experience",
				es: "experiencia profesional"
			},
			experienceItems: [
				{
					date: {en: "Jan. 2019 - Apr. 2019",es: "Ene. 2019 - Abr. 2019"},
					place: "Durango, DGO",
					company: "4+1 Creative",
					charge: {
						en: "VueJs and Vuetify FrontEnd Developer",
						es: "Desarrollador FrontEnd con VueJs y Vuetify"
					},
					tasks: [
						{
							en: "In charge of design and development of Profit Panel focused on hardware stores",
							es: "Encargado del diseño y desarrollo del Panel de Ganancias enfocado a ferreteras"
						},
						{
							en: "In charge of development of various graphs where earnings are shown",
							es: "Encargado del desarrollo de diversos gráficos donde se muestran las ganancias"
						},
						{
							en: "In charge of development of calendar to select earnings by year, month or day",
							es: "Encargado del desarrollo de calendario para seleccionar ganancias por año, mes o día"
						}
					]
				},
				{
					date: {en: "Aug. 2019 - Nov. 2019", es: "Ago. 2019 - Nov. 2019"},
					place: "Durango, DGO",
					company: "Virtua MX",
					charge: {
						en: "FrontEnd Developer of AgroNet Web Application",
						es: "Desarrollador FrontEnd de la Aplicación Web AgroNet"
					},
					tasks: [
						{
							en: "In charge of design and development of module for creating polygons on a map using the Google Maps API",
							es: "Encargado del diseño y desarrollo de módulo de creación de polígonos en mapa mediante la utilización de API de Google Maps"
						},
						{
							en: "In charge of design and development of the task module",
							es: "Encargado del diseño y desarrollo de módulo de tareas"
						},
						{
							en: "In charge of design and development of the user administration module",
							es: "Encargado del diseño y desarrollo de módulo de administración de usuarios"
						}
					]
				},
				{
					date: {en: "Sep. 2020 - Dec. 2020", es: "Sep. 2020 - Dic. 2020"},
					place: "Durango, DGO",
					company: "Sect Telecomunicaciones",
					charge: {
						en: "FrontEnd Developer of Sect Quinielas Web Application",
						es: "Desarrollador FrontEnd de la Aplicación Web Sect Quinielas"
					},
					tasks: [
						{
							en: "In charge of design and development of Login and Sign In module",
							es: "Encargado del diseño y desarrollo del Login y Signin"
						},
						{
							en: "In charge of design and development of Dashboard module, Football Pools Visualization, Creation of New Football Pools, Options Menu and Edit Profile module",
							es: "Encargado del diseño y desarrollo de módulo de Dashboard, Visualización de Quinielas, Creación de Nueva Quiniela, Menú de Opciones and Editar Perfil"
						}
					]
				}
			]
		},
		windowWidth: window.innerWidth,
	}),
	// Computed Ordered Alphabetically
	computed: {
		selectedLanguage() {
			return( this.language == "English" ? "en" : "es" )
		},

		// Custom BreakPoint of sm Col in Fullname
		smNameBreakPt() {
			if(this.windowWidth >= 785) return "auto"
			if(this.windowWidth >= 670 && this.windowWidth < 785) return "4"
			if(this.windowWidth < 670) return "3"
			return ""
		},

		// xs Col Align BreakPoint of Screen Resolution Width
		xsAlignBreakPt() {
			return( this.windowWidth < 600 ? "center" : "" )
		},

		// xs BreakPoint of Screen Resolution Width
		isXsBreakpoint() {
			return( this.windowWidth < 600 ? true : false )
		},
	},

	created() {
		this.onResize()
	},

	methods: {
		generatePDF(){
			// Loading PDF Download
			this.isButtonLoading = true
			// Display All Page Content
			this.isInfoVisible = [true, true, true, true, true]
			// Assign PDF Width an Height
			const pdfHeight = this.language == "English" ? 1544 : 1560
			const pdfWidth = Math.round(pdfHeight*(7/9.9))
			// Save PDF Generation Options
			const opts = {
				margin: 0,
				filename: 'CV_Javier_Correa.pdf',
				image: { type: 'jpeg', quality: 0.98 },
				html2canvas: {
					logging: false,
					scale: 2,
					windowWidth: 1904,
					windowHeight: pdfHeight
				},
				jsPDF: {
					unit: 'px',
					format: [pdfWidth, pdfHeight],
					hotfixes: ["px_scaling"],
					orientation: 'p'
				},
			}
			// Wait to Display All Content to Save PDF Page
			setTimeout(() => {
				this.windowWidth = 1904
				html2pdf().from(document.body)
				.then(() => this.windowWidth = innerWidth)
				.set(opts).save()
				.then(() => this.isButtonLoading = false)
			}, 300)
		},

		goToHomeRoute(){
			this.$router.push({ name: "Home" })
		},

		onResize() {
			this.windowWidth = window.innerWidth
		},

		rotate(i){
			return( this.isInfoVisible[i] ? "rotate-0deg" : "rotate-180deg" )
		},

		// Method to show and Hide Content
		showOrHide(index) {
			// If True, change False and Vice Versa
			this.$set(
				this.isInfoVisible,
				index,
				!this.isInfoVisible[index]
			)
		}
	}
};
</script>

<style scoped>
/*  Alphabetically Ordered Styles */
#app-curriculum {
	background-color: transparent
}

.icon-chevron {
	width: 15px
}

.icon-home {
	width: 30px
}

.information-background {
	background-color: rgba(225, 225, 225, 0.9)
}

.no-width {
	width: 0;
}

.select-width {
	width: 155px
}

.title {
	color: #666;
	cursor: pointer;
	display: inline;
	padding-left: 16px;
	text-transform: uppercase;
}

/* Transitions Styles */
.rotate-0deg {
	transform: rotate(0deg)
}

.rotate-180deg {
	transform: rotate(180deg)
}

.slice-enter-active, .slice-leave-active {
	transition: all 0.2s cubic-bezier(1, 0.8, 0.8, 1);
}

.slice-enter, .slice-leave-to {
	transform: translateY(-20px);
	opacity: 0.1;
	margin-bottom: 0px;
}
</style>